import { Box, Input, Typography } from "@mui/joy";
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ProgramContentType } from "data/BlockComponent";
import { useTranslation } from "react-i18next";
import { setProgramContentData } from "logic/logics";
import { theme } from "styles/theme";
import { useRecoilState } from 'recoil'
import { invalidTextareaIndexAtom } from '../../../../recoil/invalidTextareaIndexAtom'

export default function PercentageCell({
  id,
  initialPercentage,
  blockIndex,
  setData,
  lineIndex,
  cellIndex,
  inputKey,
  placeholder,
  min = 0,
  max = 100,
  step = 5,
  warnIfZero,
  readOnly = false,
}: {
  id?: string;
  initialPercentage?: number;
  blockIndex: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  inputKey?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  warnIfZero?: string;
  readOnly?: boolean;
}) {
  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  const [invalidTextareaIndexList, setInvalidTextareaIndexList] =
    useRecoilState(invalidTextareaIndexAtom);
  const indexKey = `${blockIndex}-${lineIndex}-${cellIndex}`;
  const isInvalidTextarea = invalidTextareaIndexList.includes(indexKey);
  const hadInvalidBorder = useRef(isInvalidTextarea);

  useEffect(() => {
    if (isInvalidTextarea) {
      hadInvalidBorder.current = true;
    }
  }, [isInvalidTextarea]);

  const updateInvalidIndexList = useCallback(
    (val?: number) => {
      setInvalidTextareaIndexList((prev) => {
        if (!hadInvalidBorder.current) return prev;
        const exists = prev.includes(indexKey);
        const filled = val !== undefined && !isNaN(val) && (warnIfZero ? val > 0 : true) && val !== null;
        if (filled) {
          return exists ? prev.filter((idx) => idx !== indexKey) : prev;
        }
        return exists ? prev : [...prev, indexKey];
      });
    },
    [indexKey, setInvalidTextareaIndexList, warnIfZero]
  );

  const handleChange = (percentage: number) => {
    setProgramContentData({
      setData,
      blockIndex,
      lineIndex,
      cellIndex,
      newlyAddedData: {
        value:
          percentage !== undefined && !isNaN(percentage)
            ? `${Math.max(min, Math.min(percentage, max))}`
            : undefined,
        percentage:
          percentage !== undefined && !isNaN(percentage)
            ? Math.max(min, Math.min(percentage, max))
            : undefined,
      },
    });
    updateInvalidIndexList(percentage);
  };

  useEffect(() => {
    if (initialPercentage !== undefined) {
      handleChange(initialPercentage);
    } else {
      updateInvalidIndexList(undefined);
    }
  }, [initialPercentage, updateInvalidIndexList]);

  const [isFocused, setIsFocused] = useState<boolean>(false);

  return (
    <Box>
      <Input
        id={`textarea-${indexKey}`}
        key={inputKey}
        value={initialPercentage}
        placeholder={
          placeholder ||
          (id?.startsWith("영향받는 정도") || id === "percentageThought"
            ? t_common("영향 정도 placeholder")
            : undefined)
        }
        type={min < 0 ? "number" : "tel"}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => {
          if (readOnly) return;
          const percentage = parseInt(e.target.value);
          handleChange(percentage);
        }}
        onKeyDown={(e) => {
          if (readOnly) {
            e.preventDefault();
            return;
          }
          if (e.key === "Enter") {
            if (isFocused) {
              (document.activeElement as HTMLInputElement)?.blur();
              setIsFocused(false);
            }
          }
        }}
        slotProps={{
          input: { min: min || 0, max: max || 100, step: step || 5 },
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          if (readOnly) return;
          setIsFocused(false);
          const val = parseInt((e.target as HTMLInputElement).value);
          updateInvalidIndexList(isNaN(val) ? undefined : val);
        }}
        sx={{
          backgroundColor: "white",
          borderRadius: "6px",
          borderWidth: 0,
          boxShadow: isInvalidTextarea
            ? `inset 0 0 0 1.333px ${theme.vars.palette.Border.HighlightWithAlert}`
            : isFocused
              ? `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`
              : `inset 0 0 0 1px ${theme.vars.palette.divider}`,
          px: "10px",
          py: "12px",
          "--Input-minHeight": 0,
          "--joy-focus-thickness": "0px",
          opacity: readOnly ? 1 : 1,
        }}
      />
      {warnIfZero && initialPercentage === 0 && (
        <Typography variant="Caption" sx={{ mt: 0.5, color: theme.vars.palette.Content.HighlightWithAlert, fontWeight: 500 }}>
          {warnIfZero}
        </Typography>
      )}
    </Box>
  );
}
